import useSWR from 'swr';

import type { LicenseTier, TeamBalance } from '@/api-models/balance';
import { fetchEndpointData } from '@/utils/fetch.client';

/**
 * Staff tools for a space's licence and credits.
 *
 * There is no payment provider yet, so this is the whole billing system: an
 * operator records the licence a customer bought and grants the usage credits they
 * paid for. Every movement appends to `credit_ledger`, which the database makes
 * impossible to edit afterwards — a revocation is a negative row, never a deletion.
 */

/** One movement of a space's credit balance. */
export interface CreditLedgerEntry {
  id: string;
  /** Signed: positive grants, negative revokes or settles. */
  amount_micro_usd: number;
  kind: CreditEntryKind;
  reason: string;
  /** Admin who moved it; `null` for system-written settlements. */
  granted_by: string | null;
  /** Set on settlements: the period whose overflow this row charged. */
  settled_period_start: string | null;
  created_at: string;
}

export type CreditEntryKind = 'grant' | 'revocation' | 'settlement';

export interface AdminTeamCredits extends TeamBalance {
  entries: CreditLedgerEntry[];
}

export interface RecomputeResult extends TeamBalance {
  counter_micro_usd: number;
  ledger_micro_usd: number;
  /** Ledger minus counter. Zero means the cache was already right. */
  drift_micro_usd: number;
}

function creditsKey(teamId: string) {
  return `/api/v1/admin/teams/${teamId}/credits`;
}

export function useAdminTeamCredits(teamId: string | undefined) {
  const key = teamId ? creditsKey(teamId) : null;
  const { data, error, isLoading, mutate } = useSWR(key, () => fetchEndpointData<AdminTeamCredits>(key!), {
    revalidateOnFocus: false,
  });
  return { credits: data ?? null, error, isLoading, mutate };
}

export interface UpdateLicenseInput {
  license_tier: LicenseTier;
  /** `null` counts active members instead, so adding someone raises the allowance. */
  seats: number | null;
  /** RFC 3339. `null` leaves an existing anchor alone. */
  license_started_at: string | null;
}

export function updateTeamLicense(teamId: string, input: UpdateLicenseInput) {
  return fetchEndpointData<TeamBalance>(`/api/v1/admin/teams/${teamId}/license`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

/** `amountUsd` is signed: positive grants, negative revokes. Reason is required. */
export function grantCredits(teamId: string, amountUsd: number, reason: string) {
  return fetchEndpointData<AdminTeamCredits>(creditsKey(teamId), {
    method: 'POST',
    body: JSON.stringify({ amount_usd: amountUsd, reason }),
  });
}

/** Re-derive the cached period counter from both spend ledgers. */
export function recomputeTeamPeriod(teamId: string) {
  return fetchEndpointData<RecomputeResult>(`/api/v1/admin/teams/${teamId}/recompute-period`, {
    method: 'POST',
  });
}

/** Force the lazy rollover. Idempotent — a double-click cannot settle twice. */
export function settleTeamPeriod(teamId: string) {
  return fetchEndpointData<TeamBalance>(`/api/v1/admin/teams/${teamId}/settle-period`, {
    method: 'POST',
  });
}

/**
 * Append a signed correction to a charge. The ledgers are append-only, so this is
 * the only way to change what a space owes.
 */
export function correctLlmUsage(usageId: string, costUsdDelta: number, reason: string) {
  return fetchEndpointData(`/api/v1/admin/llm-usage/${usageId}/corrections`, {
    method: 'POST',
    body: JSON.stringify({ cost_usd_delta: costUsdDelta, reason }),
  });
}

export function correctToolUsage(usageId: string, costUsdDelta: number, reason: string) {
  return fetchEndpointData(`/api/v1/admin/tool-usage/${usageId}/corrections`, {
    method: 'POST',
    body: JSON.stringify({ cost_usd_delta: costUsdDelta, reason }),
  });
}
